import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axios from "axios";
import Title from "../../../components/Title";
import { Office } from "../../../models/office";
import { Collect } from "../../../models/collect";
import { Lend } from "../../../models/lend";
import {
  useTheme,
  useMediaQuery,
  Typography,
  Box,
  Card,
  Grid,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  CardContent,
} from '@mui/material';
import { useSelection } from '../../../components/context/SelectionContext';
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import CollectByOfficeForm from "../../../components/filter/collectbyOffice";
import StickyListToolbar, {
  stickyListRegionWidth,
} from "../../../components/layouts/StickyListToolbar";
import EmptyStateMessage from "../../../components/layouts/EmptyStateMessage";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn } from "../../../components/table/interface";
import { useDrawer } from "../../../components/context/DrawerContext";
import { formatCurrency } from "../../../utils/format/currency";

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const TransferirPrestamo: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { drawerOpen } = useDrawer();
  const { canTransfer } = useResourcePermissions('/procesos/transferirprestamo');
  const formattedDate = nowOperationalDateString();
  const { office_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();

  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [collectClientBalance, setCollectClientBalance] = useState<Lend[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [office_id2, setOffice_id2] = useState("");
  const [collectsOffice2, setCollectsOffice2] = useState<Collect[]>([]);
  const [collect_id2, setCollect_id2] = useState("");
  const [selectedCollect, setSelectedCollect] = useState<(string | number)[]>([]);

  const orderedCollectClientBalance = useMemo(() => {
    return [...collectClientBalance].sort((a, b) => {
      const pa = a?.position != null ? Number(a.position) : Number.POSITIVE_INFINITY;
      const pb = b?.position != null ? Number(b.position) : Number.POSITIVE_INFINITY;
      if (pa !== pb) {
        return pa - pb;
      }
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });
  }, [collectClientBalance]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: userResp }, { data: officesResp }] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
        ]);
        setUser_id(String(userResp?.data?.id ?? ""));
        setOffices(Array.isArray(officesResp?.data) ? officesResp.data : []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    })();
  }, [setUser_id]);

  useEffect(() => {
    if (!office_id) {
      setCollectsOffice([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`collectsoffice/${office_id}`);
        setCollectsOffice(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching collects:", error);
      }
    })();
  }, [office_id]);

  useEffect(() => {
    setCollect_id2("");

    if (!office_id2) {
      setCollectsOffice2([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`collectsoffice/${office_id2}`);
        setCollectsOffice2(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching target collects:", error);
      }
    })();
  }, [office_id2]);

  useEffect(() => {
    if (!collect_id) {
      setCollectClientBalance([]);
      setSelectedCollect([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`lendcollect/${collect_id}`);
        setCollectClientBalance(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching lends:", error);
      }
    })();
  }, [collect_id]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!canTransfer) {
      showAlert('Sin permiso para transferir préstamos.', 'warning');
      return;
    }

    if (!collect_id || !collect_id2) {
      showAlert('Debe seleccionar cobro origen y cobro destino.', 'warning');
      return;
    }

    if (selectedCollect.length === 0) {
      showAlert('Debe seleccionar al menos un préstamo.', 'warning');
      return;
    }

    const selectedLendIds = selectedCollect.map((id) => String(id));
    const selectedClientIds = selectedLendIds
      .map((selectedLendId) => {
        const matchedLend = orderedCollectClientBalance.find(
          (lend) => String(lend.id) === selectedLendId,
        );
        return matchedLend?.client?.id ? String(matchedLend.client.id) : "";
      })
      .filter(Boolean);

    const formData = {
      collect_id_source: collect_id,
      date: formattedDate,
      collect_id_target: collect_id2,
      user_id: user_id,
      client_id: selectedClientIds,
      lend_id: selectedLendIds,
    };

    try {
      showLoading();
      await axios.post("/transferlends", formData);
      showAlert('Cobros transferidos correctamente.', 'success');
      setSelectedCollect([]);
      setCollect_id("");
      setOffice_id2("");
      setCollect_id2("");
      setCollectClientBalance([]);
    } catch (error) {
      console.error('Error transferring lend:', error);
      const backendMessage =
        axios.isAxiosError(error)
          ? String(error.response?.data?.error ?? error.response?.data?.message ?? '').trim()
          : '';
      showAlert(backendMessage || 'Error al transferir préstamos.', 'error');
    } finally {
      hideLoading();
    }
  };

  const rows = useMemo(() => {
    return orderedCollectClientBalance.map((lend, index) => ({
      ...lend,
      id: lend.id,
      position: lend.position ?? '',
      positionOrdinal: index + 1,
      idnumber: lend.client?.idnumber ?? '',
      fullname: lend.client?.fullname ?? '—',
      phone: lend.client?.phone ?? '',
      mainaddress: lend.client?.mainaddress ?? '',
      balanceFormatted: formatCurrency(Number(lend.balance ?? 0)),
      collectName: lend.collect?.name ?? '',
    }));
  }, [orderedCollectClientBalance]);

  const simpleColumns: SimpleColumn[] = [
    { field: 'positionOrdinal', headerName: 'Pos.', width: '56px' },
    { field: 'idnumber', headerName: 'Nit', width: '120px' },
    { field: 'fullname', headerName: 'Cliente', width: '240px', maxWidth: '240px', wrapText: true },
    { field: 'phone', headerName: 'Teléfono', width: '120px' },
    { field: 'mainaddress', headerName: 'Dirección', width: '240px', maxWidth: '240px', wrapText: true },
    { field: 'collectName', headerName: 'Cobro', width: '140px' },
    { field: 'balanceFormatted', headerName: 'Saldo', width: '110px', align: 'right' },
  ];

  return (
    <HeaderResponsive allowHorizontalOverflow sx={{ pb: { xs: 10, md: 8 } }}>
      <StickyListToolbar>
        <Title>Transferir Préstamo</Title>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: isMobile ? 1 : 2,
          }}
        >
          <PermissionButton
            allowed={canTransfer}
            deniedTooltip="Sin permiso para transferir"
            tooltip="Transferir"
            variant="contained"
            size="small"
            onClick={submit}
            startIcon={isMobile ? null : <CompareArrowsIcon />}
          >
            {isMobile ? <CompareArrowsIcon /> : "Transferir"}
          </PermissionButton>
        </Box>
      </StickyListToolbar>

      <Card
        sx={{
          width: (theme) => stickyListRegionWidth(theme, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <Box sx={{ pb: 0.5 }}>
          <CollectByOfficeForm offices={offices} collectsOffice={collectsOffice}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="office-target-select-label" shrink>Seleccionar Oficina Destino</InputLabel>
                <Select
                  labelId="office-target-select-label"
                  id="office-target-select"
                  size="small"
                  value={office_id2}
                  onChange={(event) => setOffice_id2(event.target.value)}
                  label="Seleccionar Oficina Destino"
                  required
                >
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={String(office.id)}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="collect-target-select-label" shrink>Seleccionar Cobro Destino</InputLabel>
                <Select
                  labelId="collect-target-select-label"
                  id="collect-target-select"
                  size="small"
                  value={collect_id2}
                  onChange={(event) => setCollect_id2(event.target.value)}
                  label="Seleccionar Cobro Destino"
                  required
                  disabled={!office_id2}
                >
                  {collectsOffice2
                    .filter((collect) => String(collect.id) !== String(collect_id))
                    .map((collect) => (
                      <MenuItem key={collect.id} value={String(collect.id)}>
                        {collect.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Seleccione los préstamos a transferir desde la tabla.
              </Typography>
            </Grid>
          </CollectByOfficeForm>
        </Box>

        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0.5,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={simpleColumns}
              checkboxSelection
              selectionModel={selectedCollect}
              onRowSelectionModelChange={(ids) => setSelectedCollect(ids)}
              countValue={rows.length}
              disableInternalScroll
              rowHeight={24}
              exportCsv
              exportFileName="transferir_prestamos.csv"
              wrapCellContent
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default TransferirPrestamo;
